/**
 * ZBugReports - Zod Validation Schemas for Bug Reports API
 *
 * Endpoints: /api/bug-reports/*
 * Controller: bug-reports/bug-reports.controller.ts
 *
 * Submit is multipart/form-data (text fields + up to 10 screenshots), so the
 * Body is typed as FormData for the generated client; the backend validates
 * the text fields with BugReportFieldsSchema after multer parses the parts.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const BUG_SEVERITIES = ["high", "medium", "low"] as const;
export const BugSeveritySchema = z.enum(BUG_SEVERITIES);
export type BugSeverity = z.infer<typeof BugSeveritySchema>;

/** Text fields of the multipart body — parsed server-side after multer. */
export const BugReportFieldsSchema = z.object({
  description: z.string().min(1, "Description is required").max(200),
  stepsToReproduce: z.string().min(1, "Steps to reproduce are required"),
  severity: BugSeveritySchema
});

export type BugReportFields = z.infer<typeof BugReportFieldsSchema>;

const BugReportResultSchema = z.object({
  linearIssueId: z.string(),
  linearIssueUrl: z.string()
});

export type BugReportResult = z.infer<typeof BugReportResultSchema>;

// ═══════════════════════════════════════════════════════════════
// ZBUG REPORTS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZBugReports = {
  /**
   * POST /api/bug-reports/submit
   * Submit a bug report with optional screenshots (multipart/form-data).
   * Fields: description, stepsToReproduce, severity + up to 10 "screenshots" files.
   */
  Submit: {
    method: "POST" as const,
    path: "/api/bug-reports/submit",
    Body: z.custom<FormData>(),
    Response: ApiSuccessSchema(BugReportResultSchema)
  }
} as const;
